import { useEffect, useMemo, useState } from "react";
import { Brain, Plus, Trash2, Pencil } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/authStore";
import { relTime } from "../lib/format";
import TopBar from "../components/TopBar";
import EmptyState from "../components/EmptyState";
import Modal from "../components/Modal";
import { useToasts } from "../components/Toasts";

export default function BrainPage() {
  const { user } = useAuth();
  const push = useToasts((s) => s.push);
  const canWrite = user?.role === "director" || user?.role === "admin";
  const canDelete = user?.role === "director";
  const [entries, setEntries] = useState([]);
  const [q, setQ] = useState("");
  const [tag, setTag] = useState("");
  const [editing, setEditing] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", tags: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (tag) params.set("tag", tag);
    const { data } = await api.get(`/knowledge_entries?${params.toString()}`);
    setEntries(data);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, tag]);

  const tags = useMemo(() => {
    const s = new Set();
    entries.forEach((e) => e.tags.forEach((t) => s.add(t)));
    return Array.from(s).sort();
  }, [entries]);

  const openEdit = (e) => {
    setEditing(e);
    setForm({ title: e.title, body: e.body, tags: e.tags.join(", ") });
  };

  const openNew = () => {
    setEditing(null);
    setForm({ title: "", body: "", tags: "" });
    setShowNew(true);
  };

  const submit = async (evt) => {
    evt.preventDefault();
    setSaving(true);
    const payload = {
      title: form.title.trim(),
      body: form.body.trim(),
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
    };
    try {
      if (editing) {
        await api.put(`/knowledge_entries/${editing.id}`, payload);
        push("Entry updated", "ok");
      } else {
        await api.post("/knowledge_entries", payload);
        push("Entry created", "ok");
      }
      setEditing(null);
      setShowNew(false);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      push(typeof detail === "string" ? detail : "Save failed", "err");
    } finally {
      setSaving(false);
    }
  };

  const removeEntry = async (id) => {
    if (!window.confirm("Delete this entry? This cannot be undone.")) return;
    try {
      await api.delete(`/knowledge_entries/${id}`);
      push("Deleted", "ok");
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      push(typeof detail === "string" ? detail : "Delete failed", "err");
    }
  };

  return (
    <>
      <TopBar
        title="Company Brain"
        subtitle="Canonical context every CommandLinc agent should inherit."
        actions={
          canWrite ? (
            <button className="btn primary" onClick={openNew} data-testid="brain-new-btn">
              <Plus size={15} /> New Entry
            </button>
          ) : null
        }
      />
      <div className="panel">
        <div className="filters">
          <div className="field" style={{ flex: 1, maxWidth: 360 }}>
            <label style={{ marginBottom: 3 }}>Search</label>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="title or body"
              data-testid="brain-search"
            />
          </div>
          <div className="field">
            <label style={{ marginBottom: 3 }}>Tag</label>
            <select value={tag} onChange={(e) => setTag(e.target.value)} data-testid="brain-tag">
              <option value="">All</option>
              {tags.map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
          <span className="hint">{entries.length} entries</span>
        </div>
        <div className="panel-body">
          {entries.length === 0 ? (
            <EmptyState
              title="No knowledge entries yet."
              subtitle={canWrite ? "Add architecture, decisions, personas, glossary…" : ""}
              icon={Brain}
            />
          ) : (
            entries.map((e) => (
              <div className="wide-row" key={e.id} data-testid={`brain-row-${e.id}`}>
                <div>
                  <b>{e.title}</b>
                  <p>{e.body.slice(0, 200)}{e.body.length > 200 ? "…" : ""}</p>
                  <small>
                    {e.tags.map((t) => <span key={t} className="pill" style={{ marginRight: 4 }}>{t}</span>)}
                    <span className="hint" style={{ marginLeft: 8 }}>updated {relTime(e.updated_at)}</span>
                  </small>
                </div>
                {canWrite ? (
                  <div className="row-actions">
                    <button className="btn small" onClick={() => openEdit(e)} data-testid={`brain-edit-${e.id}`}>
                      <Pencil size={12} /> Edit
                    </button>
                    {canDelete ? (
                      <button className="btn small danger" onClick={() => removeEntry(e.id)} data-testid={`brain-del-${e.id}`}>
                        <Trash2 size={12} /> Delete
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ))
          )}
        </div>
      </div>

      {(showNew || editing) ? (
        <Modal
          eyebrow={editing ? "EDIT ENTRY" : "NEW ENTRY"}
          title={editing ? "Update knowledge entry" : "Capture a new decision"}
          onClose={() => { setShowNew(false); setEditing(null); }}
          testId="brain-modal"
        >
          <form onSubmit={submit}>
            <div className="form-row">
              <label>Title</label>
              <input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                required
                data-testid="brain-title"
              />
            </div>
            <div className="form-row">
              <label>Body</label>
              <textarea
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
                required
                rows={8}
                data-testid="brain-body"
              />
            </div>
            <div className="form-row">
              <label>Tags (comma-separated)</label>
              <input
                value={form.tags}
                onChange={(e) => setForm({ ...form, tags: e.target.value })}
                placeholder="architecture, adr, glossary"
                data-testid="brain-tags"
              />
            </div>
            <div className="modal-actions">
              <button type="button" className="btn" onClick={() => { setShowNew(false); setEditing(null); }}>Cancel</button>
              <button type="submit" className="btn primary" disabled={saving} data-testid="brain-submit">
                {saving ? <span className="loader" /> : "Save"}
              </button>
            </div>
          </form>
        </Modal>
      ) : null}
    </>
  );
}
